/**
 * The browser's half of the 10-minute idle sign-out (docs/auth/console-identity-and-access.md
 * section 6.3). The server is the control - the authorizer ends a session with
 * no API call for 10 minutes - and this is the courtesy that makes it humane:
 *
 *   - pointer, key, wheel and touch are activity;
 *   - while the person is active, a heartbeat (POST /api/auth/activity) reaches
 *     the server at most once a minute, so someone reading a page without
 *     calling the API is not signed out under them;
 *   - after idleMs - warnMs with no activity, a warning counts down; any
 *     activity in that minute cancels it;
 *   - at idleMs the Console signs out, and says why.
 *
 * Pure: the clock and timers are injected, so the rules are tested without a
 * browser (idle.test.js). IdleWarning.jsx and AuthGate.jsx wire it to the DOM.
 */

export const IDLE_MS = 10 * 60 * 1000;
export const WARN_MS = 60 * 1000;
export const HEARTBEAT_MS = 60 * 1000;
export const ACTIVITY_EVENTS = Object.freeze(['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll']);

/**
 * @param {{
 *   onWarn: (secondsLeft: number) => void,
 *   onActive: () => void,        the warning is cancelled
 *   onIdle: () => void,          sign out
 *   heartbeat: () => Promise<unknown>,
 *   idleMs?: number, warnMs?: number, heartbeatMs?: number,
 *   now?: () => number, setTimer?: typeof setTimeout, clearTimer?: typeof clearTimeout,
 * }} options
 */
export function createIdleMonitor({
  onWarn, onActive, onIdle, heartbeat,
  idleMs = IDLE_MS, warnMs = WARN_MS, heartbeatMs = HEARTBEAT_MS,
  now = () => Date.now(), setTimer = setTimeout, clearTimer = clearTimeout,
}) {
  let lastActivity = now();
  let lastBeat = lastActivity;
  let warnTimer = null;
  let idleTimer = null;
  let warned = false;
  let stopped = false;

  function arm() {
    if (warnTimer) clearTimer(warnTimer);
    if (idleTimer) clearTimer(idleTimer);
    if (stopped) return;
    warnTimer = setTimer(() => {
      warned = true;
      onWarn(Math.round(warnMs / 1000));
    }, idleMs - warnMs);
    idleTimer = setTimer(() => {
      stopped = true;
      onIdle();
    }, idleMs);
  }

  arm();

  return {
    /** Something the person did. */
    activity() {
      if (stopped) return;
      const at = now();
      lastActivity = at;
      if (warned) {
        warned = false;
        onActive();
      }
      if (at - lastBeat >= heartbeatMs) {
        lastBeat = at;
        Promise.resolve().then(heartbeat).catch(() => {});
      }
      arm();
    },
    /** Seconds until the idle sign-out, for the countdown. */
    secondsLeft() {
      return Math.max(0, Math.ceil((lastActivity + idleMs - now()) / 1000));
    },
    stop() {
      stopped = true;
      if (warnTimer) clearTimer(warnTimer);
      if (idleTimer) clearTimer(idleTimer);
    },
  };
}
